import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import { Download, Mail, Loader2, Pencil } from 'lucide-react';
import { Client } from '@/types/client';
import { apiService } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';

interface PaymentItem {
  id: string;
  description: string | null;
  value: number;
  netValue: number | null;
  status: string;
  dueDate: string;
  paymentDate: string | null;
  bankSlipUrl: string | null;
  invoiceNumber: string | null;
  billingType: string;
}

function formatCurrency(value: number): string {
  // Asaas envia value em reais (ex.: 2000 = R$ 2.000,00), não em centavos
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '–';
  // Evitar -1 dia: API retorna "YYYY-MM-DD"; new Date(str) vira meia-noite UTC e no fuso local vira dia anterior
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '–';
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    PENDING: 'Aberto',
    RECEIVED: 'Recebido',
    OVERDUE: 'Vencido',
    CONFIRMED: 'Confirmado',
    REFUNDED: 'Estornado',
    RECEIVED_IN_CASH: 'Recebido em dinheiro',
  };
  return map[status] || status;
}

function statusBadgeClass(status: string): string {
  if (status === 'RECEIVED' || status === 'RECEIVED_IN_CASH' || status === 'CONFIRMED') {
    return 'bg-green-100 text-green-800';
  }
  if (status === 'OVERDUE') return 'bg-red-100 text-red-800';
  if (status === 'PENDING') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-800';
}

interface ClientFinancialModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

export const ClientFinancialModal: React.FC<ClientFinancialModalProps> = ({
  client,
  isOpen,
  onClose,
}) => {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingEmailId, setSendingEmailId] = useState<string | null>(null);
  const [editingPayment, setEditingPayment] = useState<PaymentItem | null>(null);
  const [editForm, setEditForm] = useState<{ value: number; dueDate: string; description: string }>({
    value: 0,
    dueDate: '',
    description: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const limit = 20;
  const canEditPayment = (status: string) =>
    status === 'PENDING' || status === 'OVERDUE';

  const fetchPayments = useCallback(
    async (isLoadMore: boolean) => {
      if (!client?.id) return;
      const currentOffset = isLoadMore ? offset : 0;
      if (isLoadMore) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await apiService.getClientAsaasPayments(client.id, {
          offset: currentOffset,
          limit,
        });
        const list = res.data || [];
        if (isLoadMore) {
          setPayments((prev) => [...prev, ...list]);
        } else {
          setPayments(list);
        }
        setTotalCount(res.totalCount ?? list.length);
        setHasMore(res.hasMore ?? false);
        setOffset(currentOffset + list.length);
      } catch (err: any) {
        const msg =
          err?.message ||
          err?.error ||
          (err?.response?.data?.error) ||
          'Erro ao carregar cobranças. Verifique se o ASAAS_ACCESS_TOKEN está configurado.';
        setError(msg);
        if (!isLoadMore) setPayments([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [client?.id, offset]
  );

  useEffect(() => {
    if (isOpen && client?.id) {
      setOffset(0);
      setPayments([]);
      fetchPayments(false);
    }
  }, [isOpen, client?.id]);

  const handleSendEmail = async (payment: PaymentItem) => {
    if (!client?.id) return;
    setSendingEmailId(payment.id);
    try {
      await apiService.sendClientPaymentEmail(client.id, {
        paymentId: payment.id,
        description: payment.description || 'Boleto',
      });
      toast({
        title: 'E-mail enviado',
        description: 'O boleto foi enviado para o e-mail cadastrado do cliente.',
      });
    } catch (err: any) {
      const msg =
        err?.message ||
        err?.error ||
        err?.response?.data?.error ||
        'Falha ao enviar e-mail. Verifique se o cliente tem e-mail cadastrado.';
      toast({
        title: 'Erro ao enviar e-mail',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSendingEmailId(null);
    }
  };

  const openEditDialog = (payment: PaymentItem) => {
    setEditingPayment(payment);
    setEditForm({
      value: payment.value,
      dueDate: payment.dueDate || '',
      description: payment.description ?? '',
    });
  };

  const handleSaveEdit = async () => {
    if (!client?.id || !editingPayment) return;
    setSavingEdit(true);
    try {
      await apiService.updateClientAsaasPayment(client.id, editingPayment.id, {
        value: editForm.value,
        dueDate: editForm.dueDate || undefined,
        description: editForm.description || undefined,
      });
      setEditingPayment(null);
      toast({
        title: 'Cobrança atualizada',
        description: 'Os dados da cobrança foram salvos.',
      });
      fetchPayments(false);
    } catch (err: any) {
      const msg =
        err?.message ||
        err?.error ||
        err?.response?.data?.error ||
        'Erro ao atualizar cobrança.';
      toast({
        title: 'Erro ao atualizar',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-montserrat font-bold text-gray-900">
            Cobranças – {client?.legal_name ?? client?.trade_name ?? 'Cliente'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <AtomicLoadingScreen message="Carregando cobranças..." className="h-32" />
          </div>
        ) : error ? (
          <div className="py-6 text-center">
            <p className="text-red-600">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => fetchPayments(false)}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-auto flex-1 border border-gray-200 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-montserrat font-semibold">Descrição</TableHead>
                    <TableHead className="font-montserrat font-semibold">Valor</TableHead>
                    <TableHead className="font-montserrat font-semibold">Status</TableHead>
                    <TableHead className="font-montserrat font-semibold">Vencimento</TableHead>
                    <TableHead className="font-montserrat font-semibold">Pagamento</TableHead>
                    <TableHead className="font-montserrat font-semibold text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                        Nenhuma cobrança encontrada para este cliente.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((p) => (
                      <TableRow key={p.id} className="border-b border-gray-100">
                        <TableCell className="text-gray-900 max-w-[200px] truncate" title={p.description || undefined}>
                          {p.description || '–'}
                        </TableCell>
                        <TableCell className="font-medium text-gray-900 whitespace-nowrap">
                          {formatCurrency(p.value)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(p.status)}`}
                          >
                            {statusLabel(p.status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-700 text-sm whitespace-nowrap">
                          {formatDate(p.dueDate)}
                        </TableCell>
                        <TableCell className="text-gray-700 text-sm whitespace-nowrap">
                          {formatDate(p.paymentDate)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2 flex-wrap">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={!canEditPayment(p.status)}
                              onClick={() => openEditDialog(p)}
                              className="text-gray-700 border-gray-200 hover:bg-gray-50"
                              title={canEditPayment(p.status) ? 'Editar cobrança' : 'Somente cobranças abertas ou vencidas podem ser editadas'}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Editar
                            </Button>
                            {p.bankSlipUrl && (
                              <a
                                href={p.bankSlipUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex"
                              >
                                <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                                  <Download className="w-4 h-4 mr-1" />
                                  Baixar boleto
                                </Button>
                              </a>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={sendingEmailId === p.id || !p.bankSlipUrl}
                              onClick={() => handleSendEmail(p)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              title={!p.bankSlipUrl ? 'Boleto não disponível (ex.: PIX)' : 'Enviar boleto por e-mail'}
                            >
                              {sendingEmailId === p.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Mail className="w-4 h-4 mr-1" />
                                  Enviar por e-mail
                                </>
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {hasMore && (
              <div className="pt-4 flex justify-center">
                <Button
                  variant="outline"
                  disabled={loadingMore}
                  onClick={() => fetchPayments(true)}
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Carregar mais
                </Button>
              </div>
            )}
            {totalCount > 0 && (
              <p className="text-sm text-gray-500 text-center pt-2">
                Total: {payments.length} de {totalCount} cobrança(s)
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Modal de edição de cobrança */}
    <Dialog open={!!editingPayment} onOpenChange={(open) => !open && setEditingPayment(null)}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-montserrat font-bold text-gray-900">
            Editar cobrança
          </DialogTitle>
        </DialogHeader>
        {editingPayment && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-value" className="text-gray-700">Valor (R$)</Label>
              <Input
                id="edit-value"
                type="number"
                min={0}
                step={1}
                value={editForm.value}
                onChange={(e) => setEditForm((prev) => ({ ...prev, value: Number(e.target.value) || 0 }))}
                className="bg-white border-gray-200"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description" className="text-gray-700">Descrição</Label>
              <Input
                id="edit-description"
                type="text"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                className="bg-white border-gray-200"
                placeholder="Descrição da cobrança"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-dueDate" className="text-gray-700">Data de vencimento</Label>
              <Input
                id="edit-dueDate"
                type="date"
                value={editForm.dueDate}
                onChange={(e) => setEditForm((prev) => ({ ...prev, dueDate: e.target.value }))}
                className="bg-white border-gray-200"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditingPayment(null)} disabled={savingEdit}>
            Cancelar
          </Button>
          <Button onClick={handleSaveEdit} disabled={savingEdit}>
            {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default ClientFinancialModal;
